import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router";
import { Button } from "../../../app/components/ui/button";
import { Badge } from "../../../app/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../app/components/ui/card";
import { Checkbox } from "../../../app/components/ui/checkbox";
import { Input } from "../../../app/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../app/components/ui/table";
import { Skeleton } from "../../../app/components/ui/skeleton";
import { BottomActionBar } from "../../../components/ui/BottomActionBar";
import { useIsMobile } from "../../../lib/useBreakpoint";
import { useProducts } from "../hooks/useProducts";
import { useBrands } from "../hooks/useBrands";
import { PosBatchOverrideModal } from "../components/PosBatchOverrideModal";
import { BulkPosBatchModal } from "../components/BulkPosBatchModal";
import { BarcodePrintModal } from "../components/BarcodePrintModal";
import { useAuth } from "../../../providers/AuthProvider";
import { api } from "../../../lib/api";
import { buildInventoryBulkCsv, downloadCsv, productsToInventoryBulkRows } from "../utils/inventoryBulkCsv";
import { toast } from "sonner";

function isProductActive(status: string | null | undefined): boolean {
  const normalized = (status ?? "").trim().toLowerCase();
  return normalized === "active" || normalized === "enabled" || normalized === "on";
}

function productNeedsBarcode(p: { barcode?: string | null }): boolean {
  const b = p.barcode;
  return b === null || b === undefined || String(b).trim() === "";
}

export function ProductListPage() {
  const { products, loading, error, reload } = useProducts();
  const { brands } = useBrands();
  const { token } = useAuth();
  const isMobile = useIsMobile();
  const [q, setQ] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [activeProductId, setActiveProductId] = useState<string | null>(null);
  const [toggling, setToggling] = useState<Record<string, boolean>>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkWorking, setBulkWorking] = useState(false);
  const [bulkPosOpen, setBulkPosOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);

  const brandIdToName = useMemo(() => new Map(brands.map((b) => [String(b.id), b.name])), [brands]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return products;
    return products.filter((p) => p.name.toLowerCase().includes(qq) || (p.sku ?? "").toLowerCase().includes(qq));
  }, [products, q]);

  const selectedProducts = useMemo(() => {
    return products.filter((p) => selectedIds.has(String(p.id)));
  }, [products, selectedIds]);

  const selectedCount = selectedIds.size;

  const allFilteredSelected = useMemo(() => {
    return filtered.length > 0 && filtered.every((p) => selectedIds.has(String(p.id)));
  }, [filtered, selectedIds]);

  const someFilteredSelected = useMemo(() => {
    return filtered.some((p) => selectedIds.has(String(p.id)));
  }, [filtered, selectedIds]);

  const toggleRow = useCallback((id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  const toggleSelectAllFiltered = useCallback(
    (checked: boolean) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const p of filtered) {
          const key = String(p.id);
          if (checked) next.add(key);
          else next.delete(key);
        }
        return next;
      });
    },
    [filtered],
  );

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const activeProduct = useMemo(() => {
    if (!activeProductId) return null;
    return products.find((p) => String(p.id) === String(activeProductId)) ?? null;
  }, [products, activeProductId]);

  const toggleStatus = async (productId: string, currentStatus: string | null) => {
    if (!token) return;
    if (toggling[productId]) return;

    const isActive = isProductActive(currentStatus);
    const nextStatus = isActive ? "inactive" : "active";

    setToggling((prev) => ({ ...prev, [productId]: true }));
    try {
      await api.products.update({
        token,
        id: productId,
        payload: { status: nextStatus },
      });
      await reload();
    } catch {
      // no-op
    } finally {
      setToggling((prev) => ({ ...prev, [productId]: false }));
    }
  };

  const bulkSetStatus = async (status: "active" | "inactive") => {
    if (!token || selectedProducts.length === 0) return;
    setBulkWorking(true);
    try {
      for (const p of selectedProducts) {
        await api.products.update({ token, id: p.id, payload: { status } });
      }
      await reload();
      clearSelection();
    } finally {
      setBulkWorking(false);
    }
  };

  const exportAllProducts = () => {
    if (products.length === 0) return;
    const rows = productsToInventoryBulkRows(products, brandIdToName);
    const csv = buildInventoryBulkCsv(rows);
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    downloadCsv(`products-export-all-${stamp}.csv`, csv);
  };

  const bulkExportSelected = () => {
    if (selectedProducts.length === 0) return;
    const rows = productsToInventoryBulkRows(selectedProducts, brandIdToName);
    const csv = buildInventoryBulkCsv(rows);
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    downloadCsv(`products-export-selected-${stamp}.csv`, csv);
  };

  const bulkUseFifo = async () => {
    if (!token || selectedProducts.length === 0) return;
    setBulkWorking(true);
    try {
      for (const p of selectedProducts) {
        await api.products.clearPosBatch({ token, productId: p.id });
      }
      await reload();
      clearSelection();
    } finally {
      setBulkWorking(false);
    }
  };

  const bulkGenerateBarcodes = async () => {
    if (!token || selectedProducts.length === 0) return;
    const targets = selectedProducts.filter(productNeedsBarcode);
    if (targets.length === 0) {
      toast.message("All selected products already have a barcode.");
      return;
    }
    setBulkWorking(true);
    let generated = 0;
    let failed = 0;
    try {
      for (const p of targets) {
        try {
          const res = await api.products.generateBarcode({ token, id: p.id });
          if (!res.already_exists) generated += 1;
        } catch {
          failed += 1;
        }
      }
      await reload();
      if (failed === 0) {
        if (generated > 0) {
          toast.success(`Generated ${generated} barcode${generated === 1 ? "" : "s"}.`);
        } else {
          toast.message("No new barcodes were needed (products already had barcodes).");
        }
        clearSelection();
      } else {
        toast.warning(
          `Generated ${generated} barcode${generated === 1 ? "" : "s"}; ${failed} failed. Check the list and try again.`,
        );
      }
    } finally {
      setBulkWorking(false);
    }
  };

  return (
    <div className="p-4 space-y-6 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground mb-1 md:text-3xl">Products</h1>
          <p className="text-muted-foreground">Manage your product catalog.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={exportAllProducts}
            disabled={loading || products.length === 0}
          >
            Export all
          </Button>
          <Button asChild>
            <Link to="/products/new">Create product</Link>
          </Button>
        </div>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>All products</CardTitle>
          <CardDescription>Search and review products</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2 items-center justify-between">
            <Input
              placeholder="Search products..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="max-w-md min-h-9 w-full min-w-0 flex-1"
            />
            <Button type="button" variant="outline" onClick={() => setQ("")}>
              Reset
            </Button>
          </div>

          {selectedCount > 0 ? (
            <div className="hidden flex-wrap items-center gap-2 p-3 border rounded-md bg-muted/30 md:flex">
              <span className="text-sm font-medium">
                {selectedCount} selected
              </span>
              <Button type="button" size="sm" variant="outline" onClick={clearSelection} disabled={bulkWorking}>
                Clear selection
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={bulkExportSelected} disabled={bulkWorking}>
                Export selected
              </Button>
              <Button type="button" size="sm" onClick={() => void bulkSetStatus("active")} disabled={bulkWorking}>
                Activate
              </Button>
              <Button type="button" size="sm" variant="secondary" onClick={() => void bulkSetStatus("inactive")} disabled={bulkWorking}>
                Deactivate
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => void bulkUseFifo()} disabled={bulkWorking}>
                Use FIFO (POS)
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => setBulkPosOpen(true)} disabled={bulkWorking}>
                Set POS batches…
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => void bulkGenerateBarcodes()} disabled={bulkWorking}>
                Generate barcodes
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => setPrintOpen(true)} disabled={bulkWorking}>
                Print barcodes…
              </Button>
            </div>
          ) : null}

          {error ? <div className="text-sm text-destructive">{error}</div> : null}

          <div className="hidden overflow-x-auto rounded-md border border-border md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={allFilteredSelected ? true : someFilteredSelected ? "indeterminate" : false}
                    onCheckedChange={(v) => toggleSelectAllFiltered(v === true)}
                    aria-label="Select all on this page"
                  />
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead>Barcode</TableHead>
                <TableHead>POS FIFO batch</TableHead>
                <TableHead>Categories</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">POS Batch</TableHead>
                <TableHead className="text-right min-w-0 max-w-[min(100%,220px)]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    <div className="flex flex-col gap-2 py-4">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-[75%]" />
                    </div>
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-muted-foreground">
                    No products found.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.has(String(p.id))}
                        onCheckedChange={(v) => toggleRow(String(p.id), v === true)}
                        aria-label={`Select ${p.name}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <span>{p.name}</span>
                        {p.is_override ? <Badge variant="secondary">Override Active</Badge> : null}
                      </div>
                    </TableCell>
                    <TableCell>{p.sku ?? "-"}</TableCell>
                    <TableCell className="text-sm">
                      {p.barcode ? (
                        <span className="font-mono">{String(p.barcode)}</span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {p.pos_active_batch ? (
                        <div className="space-y-0.5">
                          <div className="font-medium">Batch #{p.pos_active_batch.id}</div>
                          <div className="text-muted-foreground">
                            Sell {p.pos_active_batch.selling_price} · MRP {p.pos_active_batch.mrp} · Left{" "}
                            {p.pos_active_batch.quantity_remaining}
                            {p.available_qty_net !== undefined && p.available_qty_net !== null
                              ? ` · Available (net) ${p.available_qty_net}`
                              : ""}
                          </div>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">No stock</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {p.categories && p.categories.length > 0 ? (
                        p.categories.map((c) => c.name).join(", ")
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{p.status ?? "-"}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setActiveProductId(String(p.id));
                          setModalOpen(true);
                        }}
                      >
                        Select POS Batch
                      </Button>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        <Button asChild size="sm" variant="outline">
                          <Link to={`/products/${p.id}/edit`}>Edit</Link>
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          disabled={Boolean(toggling[p.id])}
                          onClick={() => void toggleStatus(p.id, p.status)}
                        >
                          {isProductActive(p.status) ? "Deactivate" : "Activate"}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>

          <div className="space-y-3 md:hidden">
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-32 w-full rounded-xl" />)
            ) : filtered.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">No products found.</div>
            ) : (
              filtered.map((p) => (
                <Card key={p.id} className="border-border">
                  <CardContent className="space-y-3 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <Checkbox
                        checked={selectedIds.has(String(p.id))}
                        onCheckedChange={(v) => toggleRow(String(p.id), v === true)}
                        aria-label={`Select ${p.name}`}
                      />
                      <Badge variant={isProductActive(p.status) ? "default" : "secondary"}>{p.status ?? "—"}</Badge>
                    </div>
                    <div>
                      <div className="font-medium">{p.name}</div>
                      {p.is_override ? (
                        <Badge variant="secondary" className="mt-1">
                          Override Active
                        </Badge>
                      ) : null}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <div className="text-muted-foreground text-xs">SKU</div>
                        <div>{p.sku ?? "—"}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground text-xs">Barcode</div>
                        <div className="font-mono text-xs break-all">{p.barcode ? String(p.barcode) : "—"}</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button asChild size="sm" variant="outline" className="min-h-9">
                        <Link to={`/products/${p.id}/edit`}>Edit</Link>
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        className="min-h-9"
                        disabled={Boolean(toggling[p.id])}
                        onClick={() => void toggleStatus(p.id, p.status)}
                      >
                        {isProductActive(p.status) ? "Deactivate" : "Activate"}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="min-h-9"
                        onClick={() => {
                          setActiveProductId(String(p.id));
                          setModalOpen(true);
                        }}
                      >
                        POS batch
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          <BarcodePrintModal
            open={printOpen}
            onOpenChange={setPrintOpen}
            products={products}
            preselectedIds={Array.from(selectedIds)}
          />
        </CardContent>
      </Card>

      {activeProduct ? (
        <PosBatchOverrideModal
          open={modalOpen}
          onOpenChange={(v) => {
            setModalOpen(v);
            if (!v) setActiveProductId(null);
          }}
          productId={activeProduct.id}
          productName={activeProduct.name}
          currentOverrideBatchId={activeProduct.pos_override_batch_id ?? null}
          isOverride={activeProduct.is_override ?? false}
          onDone={async () => {
            await reload();
          }}
        />
      ) : null}

      <BulkPosBatchModal
        open={bulkPosOpen}
        onOpenChange={(v) => {
          setBulkPosOpen(v);
          if (!v) {
            /* keep selection so user can retry */
          }
        }}
        products={selectedProducts}
        onDone={async () => {
          await reload();
          clearSelection();
        }}
      />

      {isMobile && selectedCount > 0 ? (
        <BottomActionBar>
          <div className="flex max-w-full gap-2 overflow-x-auto pb-1">
            <Button type="button" size="sm" variant="outline" onClick={clearSelection} disabled={bulkWorking}>
              Clear
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={bulkExportSelected} disabled={bulkWorking}>
              Export
            </Button>
            <Button type="button" size="sm" onClick={() => void bulkSetStatus("active")} disabled={bulkWorking}>
              Activate
            </Button>
            <Button type="button" size="sm" variant="secondary" onClick={() => void bulkSetStatus("inactive")} disabled={bulkWorking}>
              Deactivate
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => void bulkUseFifo()} disabled={bulkWorking}>
              FIFO
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setBulkPosOpen(true)} disabled={bulkWorking}>
              POS…
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => void bulkGenerateBarcodes()} disabled={bulkWorking}>
              Barcodes
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setPrintOpen(true)} disabled={bulkWorking}>
              Print…
            </Button>
          </div>
        </BottomActionBar>
      ) : null}
    </div>
  );
}
